import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { Eye, EyeOff } from 'lucide-react';
import { handleError } from '../../utils/errorHandler';
import Button from '../ui/Button';

interface PasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface EditPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: PasswordFormValues, helpers: FormikHelpers<PasswordFormValues>) => Promise<void>;
}

const passwordValidationSchema = Yup.object({
  oldPassword: Yup.string().required('Old password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*]/, 'Password must contain at least one symbol'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

const EditPasswordModal: React.FC<EditPasswordModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-black">Update Password</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <Formik
          initialValues={{
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
          }}
          validationSchema={passwordValidationSchema}
          onSubmit={async (values, helpers) => {
            try {
              await onSubmit(values, helpers);
              onClose();
            } catch (error) {
              handleError(error);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <label htmlFor="oldPassword" className="block text-sm font-medium text-neutral-700">
                  Old Password
                </label>
                <div className="relative">
                  <Field
                    id="oldPassword"
                    name="oldPassword"
                    type={showPasswords.oldPassword ? 'text' : 'password'}
                    className="block w-full px-4 py-2 mt-1 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('oldPassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPasswords.oldPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <ErrorMessage name="oldPassword" component="div" className="mt-1 text-sm text-danger-600" />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-neutral-700">
                  New Password
                </label>
                <div className="relative">
                  <Field
                    id="newPassword"
                    name="newPassword"
                    type={showPasswords.newPassword ? 'text' : 'password'}
                    className="block w-full px-4 py-2 mt-1 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('newPassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPasswords.newPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <ErrorMessage name="newPassword" component="div" className="mt-1 text-sm text-danger-600" />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700">
                  Confirm New Password
                </label>
                <div className="relative">
                  <Field
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showPasswords.confirmPassword ? 'text' : 'password'}
                    className="block w-full px-4 py-2 mt-1 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirmPassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPasswords.confirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <ErrorMessage name="confirmPassword" component="div" className="mt-1 text-sm text-danger-600" />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="neutral"
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-white font-semibold rounded-md transition ${
                    isSubmitting ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed' : 'bg-primary-700 hover:bg-primary-800'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="w-5 h-5 mr-2 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                        <path className="opacity-75" fill="white" d="M4 12a8 8 0 0116 0 8.001 8.001 0 01-16 0z" />
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default EditPasswordModal;
